import { useQuery } from '@api/hooks'
import PageTitle from '@components/PageTitle/PageTitle'
import { MarketBasketWithSettings } from '@domain'
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	Alert,
	AlertTitle,
	Box,
	Breadcrumbs,
	Button,
	IconButton,
	LinearProgress,
	LinearProgressProps,
	Link,
	Skeleton,
	Stack,
	Typography,
} from '@mui/material'
import MarketBasketsApi, { MarketBasketsKeys } from '@services/marketBaskets.api'
import BasketHeader from '@views/MarketBaskets/components/BasketHeader/BasketHeader'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'

import { useMarketBasketContext } from '../MarketBaskets.hooks'

export default function Exports() {
	const { marketBasketId, exportId } = useParams()
	const { isLoading: mbLoading, marketBasket } = useMarketBasketContext()

	const { data } = useQuery({
		queryKey: MarketBasketsKeys.exports({ id: marketBasketId, exportId }),
		queryFn: () => MarketBasketsApi.exports({ id: marketBasketId, exportId }),
		refetchInterval: (query) => {
			const resData = query.state.data

			if (resData && resData.progress >= 100) {
				return false
			}

			return 1000
		},
	})

	return (
		<Box width="100%">
			<PageTitle
				documentTitle={mbLoading ? '' : marketBasket.displayName}
				title={<Title marketBasket={marketBasket} isLoading={mbLoading} />}
			/>

			<Stack px="2rem" spacing={2} sx={{ width: '35rem' }}>
				<BasketHeader>
					<BasketHeader.InfoBar />
				</BasketHeader>
				<Stack spacing={2}>
					<Alert severity="info">
						<AlertTitle>Preparing excel file for export.</AlertTitle>
						Closing this page before it is complete will require restarting the export.
					</Alert>
				</Stack>

				<LinearProgressWithLabel variant="determinate" value={data?.progress ?? 0} />

				<Button
					component={RouterLink}
					to={data?.url || ''}
					target="_blank"
					rel="noopener noreferrer"
					variant="contained"
					disabled={data?.progress !== 100}
					sx={{ width: 'fit-content' }}
				>
					Download File
				</Button>
			</Stack>
		</Box>
	)
}

// FIXME dry this out, it's duplicated a couple times now
function Title({
	marketBasket,
	isLoading,
}: {
	marketBasket: MarketBasketWithSettings
	isLoading: boolean
}) {
	const navigate = useNavigate()
	const { marketBasketId } = useParams()

	return (
		<Breadcrumbs>
			<IconButton onClick={() => navigate(-1)} color="info">
				<FontAwesomeIcon icon={faChevronLeft} size="sm" />
			</IconButton>
			<Link component={RouterLink} underline="hover" color="info.light" to="/app/market-baskets">
				Market Baskets
			</Link>

			{isLoading ? (
				<Skeleton width="10rem" />
			) : (
				<Link
					component={RouterLink}
					underline="hover"
					color="info.light"
					to={`/app/market-baskets/${marketBasketId}`}
				>
					{`${marketBasket.id} - ${marketBasket.displayName}`}
				</Link>
			)}

			<Typography color="text.primary" variant="body1">
				Export
			</Typography>
		</Breadcrumbs>
	)
}

function LinearProgressWithLabel({ value, ...props }: LinearProgressProps & { value: number }) {
	return (
		<Box sx={{ display: 'flex', alignItems: 'center' }}>
			<Box sx={{ width: '100%', mr: 1 }}>
				<LinearProgress variant="determinate" {...props} value={value} sx={{ height: 10 }} />
			</Box>
			<Box sx={{ minWidth: 35 }}>
				<Typography variant="body2" sx={{ color: 'text.secondary' }}>
					{`${Math.round(value)}%`}
				</Typography>
			</Box>
		</Box>
	)
}
